import React, { useState } from 'react';
import { Title, Select, Group, ActionIcon, Loader } from '@mantine/core';
import { Plus, Trash2, Edit, Info } from 'lucide-react';
import { useTodoLists } from '../hooks/useTodos';
import { useUser } from '../contexts/UserContext';
import { useDialog } from '../contexts/DialogContext';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { todoApi } from '../api/todoApi';
import { Modal } from './Modal';
import { CreateListForm } from './CreateListForm';
import { EditListForm } from './EditListForm';
import { ListInfoDialog } from './ListInfoDialog';
import type { TodoList } from '../types/todo';

interface SharedHeaderProps {
  title: string;
  icon: React.ReactNode;
  selectedListId: number | null;
  onListChange: (listId: number | null) => void;
  additionalActions?: React.ReactNode;
  showListActions?: boolean;
}

export const SharedHeader: React.FC<SharedHeaderProps> = ({
  title,
  icon,
  selectedListId,
  onListChange,
  additionalActions,
  showListActions = true
}) => {
  const { currentUser } = useUser();
  const { confirm } = useDialog();
  const { data: lists = [], isLoading: listsLoading } = useTodoLists();
  const [showCreateList, setShowCreateList] = useState(false);
  const [showEditList, setShowEditList] = useState(false);
  const [showListInfo, setShowListInfo] = useState(false);
  const [editingList, setEditingList] = useState<TodoList | null>(null);
  const [createListData, setCreateListData] = useState<{ name: string; description?: string; color?: string } | null>(null);
  const [editListData, setEditListData] = useState<{ name: string; description?: string; color?: string; isShared?: boolean; sharedWith?: string[]; isPublic?: boolean } | null>(null);

  const queryClient = useQueryClient();

  // Create list mutation
  const createListMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; color?: string }) => 
      todoApi.createList(data, currentUser?.email || ''),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowCreateList(false);
      setCreateListData(null);
      if (newList && newList.id) {
        onListChange(newList.id);
      }
      notifications.show({
        title: 'List Created',
        message: `List "${newList.name}" has been created successfully.`,
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to create list. Please try again.',
        color: 'red',
      });
    }
  });

  // Update list mutation
  const updateListMutation = useMutation({
    mutationFn: async (data: { 
      name: string; 
      description?: string; 
      color?: string;
      isShared?: boolean;
      sharedWith?: string[];
      isPublic?: boolean;
    }) => {
      if (!editingList) return;
      
      // First update basic list info
      await todoApi.updateList(editingList.id, {
        name: data.name,
        description: data.description,
        color: data.color,
        isPublic: data.isPublic
      }, currentUser?.email || '');
      
      // Then update sharing if it changed
      const sharingChanged = data.isShared !== editingList.isShared || 
        JSON.stringify(data.sharedWith) !== JSON.stringify(editingList.sharedWith);
        
      if (sharingChanged) {
        await todoApi.updateListSharing(
          editingList.id, 
          data.isShared || false, 
          data.sharedWith || [], 
          currentUser?.email || ''
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowEditList(false);
      setEditListData(null);
      setEditingList(null);
      notifications.show({
        title: 'List Updated',
        message: `List has been updated successfully.`,
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update list. Please try again.',
        color: 'red',
      });
    }
  });

  // Delete list mutation
  const deleteListMutation = useMutation({
    mutationFn: () => {
      if (!editingList) return Promise.resolve();
      return todoApi.deleteList(editingList.id, currentUser?.email || '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowEditList(false);
      setEditListData(null);
      
      // If we deleted the currently selected list, select another one
      if (selectedListId === editingList?.id) {
        const remainingLists = lists.filter(l => l.id !== editingList?.id);
        onListChange(remainingLists.length > 0 ? remainingLists[0].id : null);
      }
      setEditingList(null);
      notifications.show({
        title: 'List Deleted',
        message: `List has been deleted.`,
        color: 'blue',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete list. Please try again.',
        color: 'red',
      });
    }
  });

  const selectedList = lists.find(l => l.id === selectedListId);

  const listOptions = lists.map(list => ({
    value: String(list.id),
    label: list.name
  }));

  const handleDeleteList = async (list: TodoList) => {
    confirm(
      'Delete List',
      `Are you sure you want to delete "${list.name}"? This action cannot be undone.`,
      async () => {
        setEditingList(list);
        deleteListMutation.mutate();
      }
    );
  };

  const handleEditList = (list: TodoList) => {
    setEditingList(list);
    setEditListData({
      name: list.name,
      description: list.description,
      color: list.color,
      isShared: list.isShared,
      sharedWith: list.sharedWith,
      isPublic: list.isPublic
    });
    setShowEditList(true);
  };

  return (
    <>
      <div style={{ 
        padding: '12px 16px',
        borderBottom: '1px solid var(--app-border)',
        flexShrink: 0,
        background: 'var(--app-surface)'
      }}>
        <Group justify="space-between" align="center">
          <Group gap="sm" align="center">
            {icon}
            <Title order={3}>{title}</Title>
          </Group>
          
          <Group gap="sm">
            {listsLoading ? (
              <Loader size="sm" />
            ) : (
              <Select
                placeholder="Select list"
                data={listOptions}
                value={selectedListId ? String(selectedListId) : null}
                onChange={(val) => onListChange(val ? parseInt(val, 10) : null)}
                style={{ width: 260 }}
              />
            )}

            {showListActions && selectedList && (
              <>
                <ActionIcon
                  variant="light"
                  onClick={() => setShowListInfo(true)}
                  title="List Info"
                  size="sm"
                >
                  <Info size={14} />
                </ActionIcon>
                
                <ActionIcon
                  variant="light"
                  onClick={() => handleEditList(selectedList)}
                  title="Edit List"
                  size="sm"
                >
                  <Edit size={14} />
                </ActionIcon>
                
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => handleDeleteList(selectedList)}
                  title="Delete List"
                  size="sm"
                >
                  <Trash2 size={14} />
                </ActionIcon>
              </>
            )}

            {showListActions && (
              <ActionIcon
                variant="light"
                onClick={() => setShowCreateList(true)}
                title="Create New List"
                size="sm"
              >
                <Plus size={14} />
              </ActionIcon>
            )}

            {additionalActions}
          </Group>
        </Group>
      </div>

      {/* Create List Modal */}
      <Modal
        isOpen={showCreateList}
        onClose={() => setShowCreateList(false)}
        title="Create New List"
        hideCloseButton={true}
        actionButtons={
          <>
            <button
              type="button"
              onClick={() => {
                if (createListData && createListData.name.trim() && createListData.color) {
                  createListMutation.mutate({
                    name: createListData.name,
                    description: createListData.description,
                    color: createListData.color,
                  });
                }
              }}
              disabled={!createListData?.name.trim() || !createListData?.color || createListMutation.isPending}
              className="header-btn header-btn-save"
              title="Create list"
              aria-label="Create list"
            >
              ✓
            </button>
            <button
              type="button"
              onClick={() => setShowCreateList(false)}
              className="header-btn header-btn-close"
              title="Cancel"
              aria-label="Cancel"
            >
              ✕
            </button>
          </>
        }
      >
        <CreateListForm
          onSave={setCreateListData}
        />
      </Modal>

      {/* Edit List Modal */}
      <Modal
        isOpen={showEditList}
        onClose={() => {
          setShowEditList(false);
          setEditListData(null);
          setEditingList(null);
        }}
        title="Edit List"
        hideCloseButton={true}
        actionButtons={
          <>
            <button
              type="button"
              onClick={() => {
                if (editListData && editListData.name.trim()) {
                  updateListMutation.mutate({
                    name: editListData.name,
                    description: editListData.description,
                    color: editListData.color,
                    isShared: editListData.isShared,
                    sharedWith: editListData.sharedWith,
                    isPublic: editListData.isPublic,
                  });
                }
              }}
              disabled={!editListData?.name.trim() || updateListMutation.isPending}
              className="header-btn header-btn-save"
              title="Save changes"
              aria-label="Save changes"
            >
              ✓
            </button>
            <button
              type="button"
              onClick={() => {
                if (editingList) {
                  handleDeleteList(editingList);
                }
              }}
              disabled={deleteListMutation.isPending || editingList?.isDefault}
              className="header-btn header-btn-delete"
              title={editingList?.isDefault ? "Cannot delete default list" : "Delete list"}
              aria-label="Delete list"
            >
              🗑
            </button>
            <button
              type="button"
              onClick={() => {
                setShowEditList(false);
                setEditListData(null);
                setEditingList(null);
              }}
              className="header-btn header-btn-close"
              title="Cancel"
              aria-label="Cancel"
            >
              ✕
            </button>
          </>
        }
      >
        {editingList && (
          <EditListForm
            list={editingList}
            onSave={setEditListData}
            onClose={() => {
              setShowEditList(false);
              setEditListData(null);
              setEditingList(null);
            }}
          />
        )}
      </Modal>

      {/* List Info Dialog */}
      {selectedList && showListInfo && (
        <ListInfoDialog
          list={selectedList}
          onClose={() => setShowListInfo(false)}
        />
      )}
    </>
  );
};
